import { Component, OnInit, OnDestroy, ViewChild, ViewContainerRef, ElementRef, TemplateRef, inject } from '@angular/core';
import { Location } from '@angular/common';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';
import { TaskState } from 'src/app/deebr/model/task.state';
import { Email, EmailStore, Template } from './email.store';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { GeneratorService } from 'src/app/deebr/service/generator.service';
import { Router } from '@angular/router';
import { FolderService, EmailFolder } from 'src/app/deebr/service/folder.service';
import { ToastService, Toast } from 'src/app/deebr/service/toast.service';
import { Enterprise, UserStore } from 'src/app/deebr/store/user.store';
import { TemplateModalComponent } from 'src/app/deebr/component/template-modal/template-modal.component';
import { DynamicImageManagementComponent } from './dynamic-image-management/dynamic-image-management.component';
import { AutomationService, AutomationCampaign } from 'src/app/deebr/service/automation.service';

const emailStore = () => inject(EmailStore);
const userStore = () => inject(UserStore);

interface Sorting {
  key: string | null;
  direction: 'asc' | 'desc' | null;
}

function isValidKey(key: string, obj: Email): boolean {
  return key in obj;
}


@Component({
  templateUrl: './generator-list.component.html',
  styleUrls: ['./generator-list.component.scss'],
  standalone: false
})
export class GeneratorListComponent implements OnInit, OnDestroy {
  private readonly emailStore = emailStore();
  private readonly userStore = userStore();
  @ViewChild('menuRef') menuElementRef!: ElementRef;
  @ViewChild('folderDialog') folderDialog!: TemplateRef<any>;
  @ViewChild('deleteDialog') deleteDialog!: TemplateRef<any>;
  @ViewChild('dialogErrorDeletingFolder') dialogErrorDeletingFolder!: TemplateRef<any>;
  @ViewChild('dialogSuccessDeletingFolder') dialogSuccessDeletingFolder!: TemplateRef<any>;
  @ViewChild('templateModal') templateModal!: TemplateModalComponent;
  private loadingToast?: Toast;

  public sorting$: BehaviorSubject<Sorting> = new BehaviorSubject({
    key: 'heure',
    direction: 'desc',
  } as Sorting);
  @ViewChild('dialog') dialog!: TemplateRef<any>;
  dialogRef: NbDialogRef<any> | undefined = undefined;
  overlayRef: OverlayRef | null = null;
  currentEmail!: any;
  showMenu: boolean = false;
  searchTerm: string = '';
  errorMessage: string = '';
  successMessage: string = '';
  displayMode: string = 'grid';
  languageList: Array<{ code: string; text: string }> = [];
  newEmailspinner: boolean = false;
  loadingFolders: boolean = false;
  loadingPreviews: Set<number> = new Set<number>();
  newFolderName: string = '';

  // Folders properties
  selectedFolder: EmailFolder | null = null;
  showFolderMenu = false;
  currentFolder: EmailFolder | null = null;
  destinationFolderId: number | null = null;
  allFolders: EmailFolder[] = [];
  public folders: EmailFolder[] = [];
  public filteredFolders: EmailFolder[] = [];
  public emails: Email[] = [];
  public filteredEmails: Email[] = [];
  invalidDestinationIds: number[] = [];
  public templates: Template[] = [];
  public selectedTemplateId: number | null = null;
  public selectedTemplateName: string | null = null;
  @ViewChild('dialogChoice') dialogChoiceTemplate!: TemplateRef<any>;
  @ViewChild('dialogAutomation') dialogAutomation!: TemplateRef<any>;

  // Propriétés pour l'avertissement suppression automation
  automationCampaignForDelete: AutomationCampaign | null = null;
  impressions7d = 0;

  // Recherche globale
  isGlobalSearchMode = false;
  allEmailsCache: Email[] | null = null;
  loadingGlobalSearch = false;
  folderNameMap: Map<number, string> = new Map();
  private folderMapLoaded = false;
  private globalSearchVersion = 0;


  constructor(
    private overlay: Overlay,
    private readonly generatorService: GeneratorService,
    private dialogService: NbDialogService,
    private viewContainerRef: ViewContainerRef,
    private router: Router,
    private folderService: FolderService,
    private toastService: ToastService,
    private location: Location,
    private automationService: AutomationService
  ) {
    this.generatorService.getTemplates().subscribe(t => this.templates = t);
  }

  openModal() {
    this.templateModal.open();
  }

  onTemplateSelect(id: number, name: string, type: string) {
    this.selectedTemplateId = id;
    this.selectedTemplateName = name;

    if (type === 'stripo_import') {
      this.newEmailspinner = true;
      this.allEmailsCache = null;
      this.emailStore.resetEmails();
      this.loadRootFoldersContent();
      this.newEmailspinner = false;
      return;
    }

    this.createEmailFromTemplate(id, name, type);
  }
  openV2(id: number) {
    // Capture l'URL EXACTE (path + child routes + params)
    const previousUrl = this.location.path(true);
    localStorage.setItem('editor_previous_url', previousUrl);

    this.router.navigate(['/pages/generator-v2', id]);
  }




  createEmailFromTemplate(templateId: number, name: string, type: string) {
    this.newEmailspinner = true;

    this.generatorService.createEmailFromTemplate(templateId, name, type).subscribe({
      next: ({ message, id }) => {
        this.allEmailsCache = null;
        this.emailStore.resetEmails();
        this.loadRootFoldersContent();
      },
      error: err => console.error('Erreur création email :', err),
      complete: () => this.newEmailspinner = false,
    });
  }
  public emails$: Observable<Email[]> = combineLatest([this.emailStore.selectEmails(), this.sorting$]).pipe(
    map(([emails, sorting]) =>
      emails.sort((a: any, b: any) =>
        sorting.key
          ? sorting.direction === 'asc'
            ? a[sorting.key] < b[sorting.key]
              ? -1
              : 1
            : sorting.direction === 'desc'
              ? a[sorting.key] < b[sorting.key]
                ? 1
                : -1
              : 1
          : 1,
      ),
    ),
  );

  public loading$: Observable<boolean> = this.emailStore.state$.pipe(
    map((state) => state.emails.loading === TaskState.InProgress),
  );

  public showDynamic$: Observable<boolean> = this.userStore
    .selectEnterprise()
    .pipe(map((enterprise: Enterprise | null) => !!enterprise?.dynamic_html));

  public showInter$: Observable<boolean> = this.userStore
    .selectEnterprise()
    .pipe(map((enterprise: Enterprise | null) => !!enterprise?.url_inter?.trim()));


  onSearchTermChange(term: string): void {
    this.searchTerm = term;
    this.searchTerm$.next(term);
  }

  getFolderName(email: Email): string | null {
    if (!email.folder_id) return null;
    return this.folderNameMap.get(email.folder_id) ?? null;
  }

  private loadFolderNameMap(): void {
    if (this.folderMapLoaded) return;
    this.folderService.getAllFolders().subscribe({
      next: (response) => {
        this.folderNameMap.clear();
        response.folders.forEach(folder => this.folderNameMap.set(folder.id, folder.name));
        this.folderMapLoaded = true;
      },
      error: (err) => console.error('Error loading folder name map:', err)
    });
  }

  private performGlobalSearch(term: string): void {
    this.loadFolderNameMap();
    if (this.allEmailsCache) {
      this.applyGlobalFilter(term);
    } else {
      // A response started before the cache was invalidated must be ignored
      const version = ++this.globalSearchVersion;
      this.loadingGlobalSearch = true;
      this.generatorService.getEmails().subscribe({
        next: (emails) => {
          if (version !== this.globalSearchVersion) return;
          this.allEmailsCache = emails;
          this.loadingGlobalSearch = false;
          this.applyGlobalFilter(this.searchTerm);
        },
        error: (err) => {
          if (version !== this.globalSearchVersion) return;
          console.error('Error loading all emails:', err);
          this.loadingGlobalSearch = false;
        }
      });
    }
  }

  /** Re-apply the active view: global search results, or the root folder contents */
  private refreshLists(): void {
    if (this.searchTerm.trim()) {
      this.isGlobalSearchMode = true;
      this.performGlobalSearch(this.searchTerm);
    } else {
      this.isGlobalSearchMode = false;
      this.filterEmails();
      this.filterFolders();
    }
  }

  private applyGlobalFilter(term: string): void {
    if (!this.allEmailsCache) return;
    const lowerTerm = term.toLowerCase();
    this.filteredEmails = this.allEmailsCache.filter(email =>
      email.name?.toLowerCase().includes(lowerTerm)
    );
    this.filteredFolders = [];
    this.sortEmails();
  }

  private searchTerm$ = new BehaviorSubject<string>('');

  ngOnInit() {
    this.loadRootFoldersContent();
    const savedDisplayMode = sessionStorage.getItem('displayMode');
    if (savedDisplayMode) {
      this.displayMode = savedDisplayMode;
    }
    document.addEventListener('click', this.closeMenu.bind(this));
    this.searchTerm$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      if (!term.trim()) {
        this.isGlobalSearchMode = false;
        this.filterEmails();
        this.filterFolders();
      } else {
        this.isGlobalSearchMode = true;
        this.performGlobalSearch(term);
      }
    });
    this.emails$ = combineLatest([
      this.emailStore.selectEmails(),
      this.sorting$,
      this.searchTerm$.asObservable()
    ]).pipe(
      map(([emails, sorting, searchTerm]) => {
        // First filter by folder_id being null
        let filteredEmails = emails.filter(email => email.folder_id === null);

        // Then filter by search term if one exists
        if (searchTerm) {
          filteredEmails = filteredEmails.filter(email =>
            email.name?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        // Apply sorting
        return filteredEmails.sort((a: Email, b: Email) => {
          if (sorting.key && sorting.key in a && sorting.key in b) {
            const sortValue1 = a[sorting.key as keyof Email];
            const sortValue2 = b[sorting.key as keyof Email];

            // Check if values are defined before comparing
            if (sortValue1 !== undefined && sortValue2 !== undefined) {
              if (sorting.direction === 'asc') {
                return sortValue1 < sortValue2 ? -1 : 1;
              } else if (sorting.direction === 'desc') {
                return sortValue1 > sortValue2 ? -1 : 1;
              }
            }
          }
          return 0;
        });
      })
    );
  }



  ngOnDestroy() {
    document.removeEventListener('click', this.closeMenu.bind(this));
    this.emailStore.resetEmails();
  }

  /** Apply the current search term, then sort */
  filterEmails(): void {
    const term = this.searchTerm.toLowerCase();
    if (!term) {
      this.filteredEmails = [...this.emails];
    } else {
      this.filteredEmails = this.emails.filter(email =>
        email.name?.toLowerCase().includes(term)
      );
    }
    this.sortEmails();
  }

  private filterFolders(): void {
    const term = this.searchTerm.toLowerCase();
    if (!term) {
      this.filteredFolders = [...this.folders];
    } else {
      this.filteredFolders = this.folders.filter(folder =>
        folder.name?.toLowerCase().includes(term),
      );
    }
    this.sortFolders();
  }

  /** Sorts `filteredEmails` based on the sorting$ BehaviorSubject */
  sortEmails(): void {
    const sorting = this.sorting$.getValue();
    if (sorting.key) {
      this.filteredEmails.sort((a: Email, b: Email) => {
        const aVal = a[sorting.key as keyof Email];
        const bVal = b[sorting.key as keyof Email];
        if (aVal !== undefined && bVal !== undefined) {
          return sorting.direction === 'asc'
            ? (aVal < bVal ? -1 : 1)
            : (aVal < bVal ? 1 : -1);
        }
        return 0;
      });
    }
  }

  private sortFolders(): void {
    const dir = this.sorting$.getValue().direction;
    this.filteredFolders.sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return dir === 'asc' ? aTime - bTime : bTime - aTime;
    });
  }

  onSort(): void {
    const current = this.sorting$.getValue();
    this.sorting$.next({
      key: current.key,
      direction: current.direction === 'asc' ? 'desc' : 'asc'
    });
    this.sortEmails();
    this.sortFolders();
  }

  createEmptyEmail(folder_id?: number | null) {
    this.newEmailspinner = true; // Affiche le spinner
    console.log(this.newEmailspinner);

    // Call the updated service method with optional folder ID
    this.generatorService.createEmptyEmail(folder_id).subscribe({
      next: (response) => {
        console.log(response.message); // Affiche : "Generated HTML created successfully"
        console.log(response.id);      // Affiche l'ID, par exemple : 1628

        this.allEmailsCache = null;
        this.emailStore.resetEmails();
        this.loadRootFoldersContent();
      },
      error: (err) => {
        console.error("Erreur lors de la création de l'email :", err);
      },
      complete: () => {
        this.newEmailspinner = false; // Cache le spinner après la fin de la requête
        console.log(this.newEmailspinner);
      }
    });
  }

  toggleOptionsMenu(event: MouseEvent, email: any): void {
    event.stopPropagation();

    // Si le menu actuel est déjà ouvert pour cet email, fermez-le.
    if (this.showMenu && this.currentEmail === email) {
      this.showMenu = false;
      this.currentEmail = null;
    } else {
      // Sinon, ouvrez le menu pour l'email cliqué.
      this.currentEmail = email;
      this.showMenu = true;
    }
  }
  closeMenu(event: MouseEvent): void {
    // Close email menu
    this.showMenu = false;

    // Also close folder menu
    this.showFolderMenu = false;
  }

  askDeleteEmail(email: Email) {
    this.currentEmail = email;
    if (email.campaign_id) {
      this.automationService.getCampaignsByCampaignId(email.campaign_id).subscribe(campaigns => {
        const activeCampaign = campaigns.find(c => c.status === 'ACTIVE');
        if (activeCampaign) {
          this.automationCampaignForDelete = activeCampaign;
          this.automationService.getCampaignMetrics(activeCampaign.id, 7).subscribe({
            next: (metrics) => {
              this.impressions7d = metrics.total_impressions;
              this.dialogRef = this.dialogService.open(this.dialogAutomation);
            },
            error: () => {
              this.impressions7d = 0;
              this.dialogRef = this.dialogService.open(this.dialogAutomation);
            },
          });
        } else {
          this.automationCampaignForDelete = null;
          this.dialogRef = this.dialogService.open(this.dialog);
        }
      });
    } else {
      this.dialogRef = this.dialogService.open(this.dialog);
    }
  }
  public deleteEmail(email: Email, dialogRef: any): void {
    const subscription = this.emailStore.deleteEmailCompleted.subscribe(() => {
      this.allEmailsCache = null;
      // Remove the email from our local arrays
      this.emails = this.emails.filter(e => e.id !== email.id);
      this.refreshLists(); // Re-apply filters and sorting
      subscription.unsubscribe();
    });
    this.emailStore.deleteEmail({ id: email.id });
    dialogRef.close();
  }
  public copyEmail(email: Email): void {
    this.loadingToast = this.toastService.loading(
      'Duplication en cours',
      `L'email "${email.name}" est en train d'être dupliqué`
    );
    const subscription = this.emailStore.copyEmailCompleted.subscribe(() => {
      if (this.loadingToast) {
        this.toastService.hideLoading(this.loadingToast);
        this.loadingToast = undefined;
      }
      this.allEmailsCache = null;
      this.loadRootFoldersContent();
      subscription.unsubscribe();
    });
    this.emailStore.copyEmail({ id: email.id, name: email.name + " (copie)" });
  }
  public openDynamicChoiceDialog(email: Email): void {
    this.dialogService.open(this.dialogChoiceTemplate, {
      context: { email }
    });
  }

  public onDynamicTypeSelected(ref: any, email: Email, useCategory: boolean): void {
    ref.close(); // Fermer la popin

    const subscription = this.emailStore.copyEmailCompleted.subscribe(() => {
      this.allEmailsCache = null;
      this.loadRootFoldersContent();
      subscription.unsubscribe();
    });

    this.emailStore.createDynamicEmail({
      id: email.id,
      name: email.name + " (dynamic)",
      useCategory: useCategory
    });
  }


  public getLangageCode(dialog: TemplateRef<any>): void {
    this.generatorService.getLangagecode(1).subscribe((data: any) => {
      this.languageList = Object.entries(data).map(([code, text]) => ({ code, text: text as string }));
      this.dialogService.open(dialog, {
        context: 'Choisissez une langue:',
        hasBackdrop: true,
      });
    });
  }

  selectLanguage(email: Email, langCode: string, dialogRef: any): void {
    dialogRef.close();
    const subscription = this.emailStore.translateEmailCompleted.subscribe(() => {
      this.allEmailsCache = null;
      this.loadRootFoldersContent();
      subscription.unsubscribe();
    });
    // Appel API après sélection de la langue
    console.log("traduction en cours")
    this.emailStore.translateEmail({ id: email.id, langage_code: langCode });
  }

  public createInterEmail(email: Email): void {
    const subscription = this.emailStore.interEmailCompleted.subscribe(() => {
      this.allEmailsCache = null;
      this.loadRootFoldersContent();
      subscription.unsubscribe();
    });
    this.emailStore.createInterEmail({ id: email.id });
  }

  /** returns [folderId, ...all its descendants' ids] */
  private getInvalidDestinationIds(
    folders: EmailFolder[],
    folderToMoveId: number
  ): number[] {
    let ids: number[] = [];

    // find the node in the tree
    const findNode = (items: EmailFolder[]): EmailFolder | undefined => {
      for (const f of items) {
        if (f.id === folderToMoveId) return f;
        if (f.children) {
          const found = findNode(f.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    const node = findNode(folders);
    if (!node) return [];

    // collect its id + all children recursively
    const collect = (f: EmailFolder) => {
      ids.push(f.id);
      if (f.children) f.children.forEach(collect);
    };
    collect(node);
    return ids;
  }

  onDisplayModeChange(): void {
    sessionStorage.setItem('displayMode', this.displayMode);
  }

  // Load the full “root” folder contents (folders emails breadcrumb)
  loadRootFoldersContent(): void {
    // this.loadingFolders = true;
    this.folderService.getFolderContents('root').subscribe({
      next: (data) => {
        // same shape as in FolderViewComponent:
        this.currentFolder = data.current_folder;
        this.folders = data.folders;
        this.filteredFolders = [...data.folders];
        this.emails = data.emails;
        // Contents changed (move, copy, rename...): global search data is stale
        this.allEmailsCache = null;
        this.folderMapLoaded = false;
        this.globalSearchVersion++;
        this.loadingGlobalSearch = false;
        this.refreshLists();
        this.loadingFolders = false;



        // Smart Check:
        // 1. Missing previews (!preview_img_url)
        // 2. Modified since last check (modified_at > lastCheckTime)
        const lastCheckTime = parseInt(localStorage.getItem('last_preview_check_time') || '0', 10);

        console.log('Checking previews. Last check:', new Date(lastCheckTime).toISOString());

        const idsToCheck = this.emails.filter(e => {
          if (!e.preview_img_url) {
            // Must have modified_at to be checked safely? 
            // User said: "Emails without 'modified_at' should be removed from the ID candidate list, so that it doesn't generate errors."
            // So if no modified_at, we exclude it even if no preview?
            // Or maybe just for the modification check? 
            // "without modified_at should be removed ... so it doesn't generate errors" implies backend needs it or logic fails.
            // Backend checks modified_at. If missing, it crashes?
            // Let's exclude if no modified_at is present at all, as per user request.
            return !!e['modified_at'];
          }
          if (e['modified_at']) {
            const modTime = new Date(e['modified_at']).getTime();
            return modTime > lastCheckTime;
          }
          return false;
        }).map(e => e.id);

        console.log('Candidate IDs:', idsToCheck);

        if (idsToCheck.length > 0) {
          // Mark these as loading
          idsToCheck.forEach(id => this.loadingPreviews.add(id));

          this.folderService.checkPreviews(idsToCheck).subscribe({
            next: (res: any) => {
              // Stream returns single result objects
              console.log('Stream result:', res);

              if (res.error) {
                console.error('Error for email', res.id, res.error);
                this.loadingPreviews.delete(res.id);
                return;
              }

              const email = this.emails.find(e => e.id === res.id);
              if (email && res.preview_img_url) {
                // Update URL and append a timestamp to bust the browser cache
                email.preview_img_url = `${res.preview_img_url}?t=${Date.now()}`;
              }
              // Remove from loading set
              this.loadingPreviews.delete(res.id);

              // Verify if all done? (Optional, but we remove from loading set individually so it's fine)
            },
            error: (err) => {
              console.error('Error checking previews:', err);
              // Clear loading state on error
              idsToCheck.forEach(id => this.loadingPreviews.delete(id));
            },
            complete: () => {
              // Update last check time only when stream completes successfully
              localStorage.setItem('last_preview_check_time', Date.now().toString());
            }
          });
        }



        this.folders.forEach(folder => {
          this.folderService.getFolderContents(folder.id).subscribe(childData => {
            folder.email_count = childData.emails.length + childData.folders.length;
            console.log('folder.id =', folder.id, 'childData =', childData);
          });
        });
      },
      error: (err) => {
        console.error('Error loading folder contents:', err);
        this.loadingFolders = false;
      }
    });
  }

  // Navigate to folder
  navigateToFolder(folderId: number) {
    this.router.navigate(['/pages/generator/folder', folderId]);
  }

  // Create folder dialog
  openCreateFolderDialog(dialog: TemplateRef<any>) {
    this.newFolderName = 'Nouveau dossier';
    this.selectedFolder = null;
    this.dialogService.open(dialog, {
      context: 'Créer un nouveau dossier',
    });
  }

  // Save new folder
  createFolder(dialogRef: NbDialogRef<any>) {
    if (this.newFolderName.trim()) {
      this.folderService.createFolder(this.newFolderName, 'root').subscribe({
        next: () => {
          this.loadRootFoldersContent();
          dialogRef.close();
        },
        error: (err) => console.error('Error creating folder:', err)
      });
    }
  }

  // Edit folder dialog
  openEditFolderDialog(folder: EmailFolder, dialog: TemplateRef<any>) {
    this.selectedFolder = folder;
    this.newFolderName = folder.name;
    this.dialogService.open(dialog, {
      context: 'Edit folder name'
    });
  }

  // Save edited folder
  updateFolder(dialogRef: NbDialogRef<any>) {
    if (this.selectedFolder && this.newFolderName.trim()) {
      this.folderService.updateFolder(this.selectedFolder.id, { name: this.newFolderName }).subscribe({
        next: () => {
          this.loadRootFoldersContent();
          dialogRef.close();
        },
        error: (err) => console.error('Error updating folder:', err)
      });
    }
  }

  // Delete folder confirmation
  confirmDeleteFolder(folder: EmailFolder, dialog: TemplateRef<any>) {
    this.selectedFolder = folder;
    this.dialogService.open(dialog, {
      context: 'Are you sure you want to delete this folder?'
    });
  }

  // Delete folder
  deleteFolder(dialogRef: NbDialogRef<any>) {
    if (this.selectedFolder) {
      this.folderService.deleteFolder(this.selectedFolder.id).subscribe({
        next: (response) => {
          this.loadRootFoldersContent();
          this.successMessage = response.message;
          this.dialogService.open(this.dialogSuccessDeletingFolder);
          dialogRef.close();
        },
        error: (err) => {
          console.error('Error deleting folder:', err);
          // Optionally, you can show an error message to the user
          this.errorMessage = 'Erreur lors de la suppression du dossier : ' + err.error.error;
          this.dialogService.open(this.dialogErrorDeletingFolder);
        }
      });
    }
  }

  // Move email to folder dialog
  openMoveEmailDialog(email: Email, dialog: TemplateRef<any>) {
    this.currentEmail = email;
    // Load all folders for the tree view
    this.loadAllFolders(() => {
      this.destinationFolderId = email.folder_id || null;
      this.dialogService.open(dialog, {
        context: 'Select destination folder'
      });
    });
  }

  // Move email to folder
  moveEmailToFolder(folderId: number | null, dialogRef: NbDialogRef<any>) {
    if (!this.currentEmail) return;
    if (this.currentEmail) {
      dialogRef.close();
      this.loadingToast = this.toastService.loading(
        'Déplacement en cours',
        `L'objet sélectionné est en train d'être déplacé`
      );
      this.folderService.moveEmailToFolder(this.currentEmail.id, folderId, this.currentEmail.name, true).subscribe({
        next: () => {
          this.allEmailsCache = null;
          this.toastService.hideLoading(this.loadingToast!);
          this.toastService.success(
            'Déplacement réussi',
            `L'objet sélectionné a bien été déplacé`
          );
          this.loadingToast = undefined;
          // refresh content
          this.loadRootFoldersContent();
          // Optionally, you can also reset the emails in the store
          this.emailStore.resetEmails();
        },
        error: (err) => {
          this.toastService.hideLoading(this.loadingToast!);
          this.loadingToast = undefined;
          this.toastService.toasts.push({
            title: 'Erreur',
            message: `Impossible de déplacer l'objet`,
            status: 'error',
            autohide: true,
          });
          console.error('Error moving email:', err);
        },
      });
    }
  }

  toggleFolderMenu(event: MouseEvent, folder: EmailFolder) {
    event.stopPropagation();

    if (this.showFolderMenu && this.currentFolder === folder) {
      this.showFolderMenu = false;
      this.currentFolder = null;
    } else {
      this.showFolderMenu = true;
      this.currentFolder = folder;
    }
  }

  duplicateFolder(folder: EmailFolder) {
    // Create a copy of the folder
    const newName = `${folder.name} (copy)`;
    this.folderService.createFolder(newName, 'root').subscribe({
      next: () => {
        this.loadRootFoldersContent();
        this.showFolderMenu = false;
      },
      error: (err) => console.error('Error duplicating folder:', err)
    });
  }

  // Move folder dialog
  openMoveFolderDialog(folder: EmailFolder, dialog: TemplateRef<any>) {
    this.selectedFolder = folder;

    // Reload tree data, then…
    this.loadAllFolders(() => {
      // 1) set the initial “parent” as default
      this.destinationFolderId = folder.parent_id;

      // 2) compute the “taboo” list so sub-folders can't be picked
      //    (uses the freshly loaded `allFolders`)
      this.invalidDestinationIds = this.getInvalidDestinationIds(
        this.allFolders,
        folder.id
      );

      // 3) now open the dialog
      this.dialogService.open(dialog, {
        context: 'Select destination folder',
      });
    });
  }

  // Move folder
  moveFolder(parentId: number | null, dialogRef: NbDialogRef<any>) {
    if (!this.selectedFolder || parentId === this.selectedFolder.id) {
      return;
    }
    if (this.selectedFolder) {
      // Check to prevent circular references
      if (parentId !== this.selectedFolder.id) {
        dialogRef.close();
        this.loadingToast = this.toastService.loading(
          'Déplacement en cours',
          `L'objet sélectionné est en train d'être déplacé`
        );
        this.folderService.updateFolder(this.selectedFolder.id, { parent_id: parentId }).subscribe({
          next: () => {
            this.toastService.hideLoading(this.loadingToast!);
            this.toastService.success(
              'Déplacement réussi',
              `L'objet sélectionné a bien été déplacé`
            );
            this.loadingToast = undefined;
            // refresh content
            this.loadRootFoldersContent();
            dialogRef.close();
          },
          error: (err) => {
            // always hide loading if error
            this.toastService.hideLoading(this.loadingToast!);
            this.loadingToast = undefined;

            // optionally show error toast
            this.toastService.toasts.push({
              title: 'Erreur',
              message: `Impossible de déplacer l'objet`,
              status: 'error',
              autohide: true,
            });

            console.error('Error moving folder:', err);
          },
        });
      }
    }
  }

  private buildFolderTree(flatFolders: EmailFolder[]): EmailFolder[] {
    // Create a dictionary for quick lookups with folder IDs as keys
    const folderMap: { [key: number]: EmailFolder & { children: EmailFolder[] } } = {};

    // Initialize the children array for each folder
    flatFolders.forEach(folder => {
      folderMap[folder.id] = { ...folder, children: [] };
    });

    // Organize folders into a hierarchical structure
    const rootFolders: EmailFolder[] = [];

    Object.values(folderMap).forEach(folder => {
      if (folder.parent_id === null) {
        // This is a root-level folder
        rootFolders.push(folder);
      } else {
        // This is a child folder, add it to its parent's children array
        const parent = folderMap[folder.parent_id];
        if (parent) {
          parent.children.push(folder);
        } else {
          // If parent doesn't exist, treat as root folder
          rootFolders.push(folder);
        }
      }
    });

    return rootFolders;
  }

  private loadAllFolders(callback?: () => void): void {
    this.folderService.getAllFolders().subscribe({
      next: (response) => {
        console.log("response:", response);
        console.log("All folders received:", response.folders);

        // Build hierarchical tree from flat list
        const folderTree = this.buildFolderTree(response.folders);
        console.log("Hierarchical folder structure:", folderTree);

        this.allFolders = folderTree;
        if (callback) callback();
      },
      error: (err) => console.error('Error loading all folders:', err)
    });
  }

  onDragStart(event: DragEvent, email: Email) {
    const sourceEl = event.currentTarget as HTMLElement;
    sourceEl.classList.add('dragging');

    event.dataTransfer!.setData('application/json', JSON.stringify({ id: email.id, name: email.name }));
    event.dataTransfer!.effectAllowed = 'move';

    const clone = sourceEl.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.top = '-1000px';
    clone.style.left = '-1000px';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '1';
    clone.style.width = `200px`;
    document.body.appendChild(clone);

    const rect = sourceEl.getBoundingClientRect();
    event.dataTransfer!.setDragImage(clone, rect.width / 2, rect.height / 2);

    // remove clone after drag image is captured
    setTimeout(() => document.body.removeChild(clone), 0);
  }

  onDragEnd(event: DragEvent) {
    const sourceEl = event.currentTarget as HTMLElement;
    sourceEl.classList.remove('dragging');
  }

  /** Allow the folder element to become a drop target */
  onDragOver(event: DragEvent) {
    event.preventDefault();
    const el = event.currentTarget as HTMLElement;
    if (!el.classList.contains('drag-over')) {
      el.classList.add('drag-over');
    }
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragEnter(event: DragEvent) {
    (event.currentTarget as HTMLElement).classList.add('drag-over');
  }
  onDragLeave(event: DragEvent) {
    const el = event.currentTarget as HTMLElement;
    const { left, right, top, bottom } = el.getBoundingClientRect();
    const x = event.clientX, y = event.clientY;

    // if pointer is still inside the bounds, do nothing
    if (x >= left && x <= right && y >= top && y <= bottom) {
      return;
    }
    el.classList.remove('drag-over');
  }

  /** When user drops the email onto a folder */
  onDrop(event: DragEvent, folder: EmailFolder) {
    event.preventDefault();
    const el = event.currentTarget as HTMLElement;
    el.classList.remove('drag-over');

    const data = event.dataTransfer?.getData('application/json');
    if (!data) return;
    const { id, name } = JSON.parse(data);

    this.loadingToast = this.toastService.loading(
      'Déplacement en cours',
      `L'objet sélectionné est en train d'être déplacé`
    );

    this.folderService
      .moveEmailToFolder(id, folder.id, name, true)
      .subscribe({
        next: () => {
          this.allEmailsCache = null;
          this.toastService.hideLoading(this.loadingToast!);
          this.toastService.success(
            'Déplacement réussi',
            `L'objet sélectionné a bien été déplacé`
          );
          this.loadingToast = undefined;

          // refresh content
          this.loadRootFoldersContent();
        },
        error: (err) => {
          this.toastService.hideLoading(this.loadingToast!);
          this.loadingToast = undefined;
          this.toastService.toasts.push({
            title: 'Erreur',
            message: `Impossible de déplacer l'objet`,
            status: 'error',
            autohide: true,
          });
          console.error('Error moving email:', err);
        },
      });
  }
}
